import { useState } from 'react'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { toast } from '@/helpers'
import { crmErrorMessage } from '@/pages/session/profile/crm-error'
import { CRM_UFS } from '@/pages/session/profile/profile.constants'
import { saveProfile } from '@/pages/session/profile/save-profile'

type CrmDialogMode = 'switch' | 'register'

type CrmDialogProps = {
  open: boolean
  onClose: () => void
  mode: CrmDialogMode
  onBack?: () => void
}

const copy: Record<
  CrmDialogMode,
  { title: string; intro: string; submit: string; success: string }
> = {
  switch: {
    title: 'Atualizar perfil para Médico',
    intro:
      'Informe seu CRM para ver os produtos para médicos. Vamos conferir o registro antes de liberar o Crédito para médicos.',
    submit: 'Atualizar perfil',
    success:
      'Perfil atualizado para Médico. Agora você vê o Crédito para médicos.',
  },
  register: {
    title: 'Cadastrar CRM',
    intro:
      'Informe seu CRM. Vamos conferir o registro no CFM antes de guardar.',
    submit: 'Cadastrar CRM',
    success: 'CRM cadastrado.',
  },
}

export function CrmDialog({ open, onClose, mode, onBack }: CrmDialogProps) {
  const [crm, setCrm] = useState('')
  const [uf, setUf] = useState('')
  const [saving, setSaving] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const text = copy[mode]

  const digits = crm.replace(/\D/g, '')
  const crmError = submitted && (digits.length < 4 || digits.length > 7)
  const ufError = submitted && uf === ''

  async function save() {
    setSubmitted(true)
    if (digits.length < 4 || digits.length > 7 || uf === '' || saving) return

    setSaving(true)
    setServerError(null)
    try {
      await saveProfile({
        practice_area: 'Medicina',
        occupation: 'doctor',
        crm: digits,
        crm_uf: uf,
      })
      toast.success(text.success)
      onClose()
    } catch (error) {
      const message = crmErrorMessage(error)
      if (message) {
        setServerError(message)
      } else {
        toast.error('Não foi possível conferir o CRM agora. Tente de novo.')
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="credit-crm-title"
    >
      <DialogTitle id="credit-crm-title">{text.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {text.intro}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: 1.5,
            mt: 2,
          }}
        >
          <TextField
            label="Número do CRM"
            value={crm}
            onChange={(event) => {
              setServerError(null)
              setCrm(event.target.value.replace(/\D/g, '').slice(0, 7))
            }}
            placeholder="Ex.: 123456"
            slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 7 } }}
            error={crmError || serverError !== null}
            helperText={
              serverError ??
              (crmError
                ? digits
                  ? 'O CRM tem de 4 a 7 números.'
                  : 'Informe o número do CRM.'
                : undefined)
            }
          />
          <TextField
            select
            label="UF do CRM"
            value={uf}
            onChange={(event) => {
              setServerError(null)
              setUf(event.target.value)
            }}
            error={ufError}
            helperText={ufError ? 'Selecione a UF do CRM.' : undefined}
          >
            {CRM_UFS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {mode === 'switch' && (
          <Typography variant="body2" color="text.secondary" mt={2}>
            Ao salvar, seu perfil deixa de ser Estudante. Mesada e Financiamento
            Estudantil saem da lista; solicitações já abertas continuam.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button variant="text" onClick={mode === 'switch' ? onBack : onClose}>
          {mode === 'switch' ? 'Voltar' : 'Cancelar'}
        </Button>
        <LoadingButton loading={saving} onClick={save}>
          {saving ? 'Conferindo CRM...' : text.submit}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
